import {
  CustomerSupportType,
  Feature,
  isFeatureAvailable,
  PaymentCardType,
} from '@shinetools/pricing-plan-library';

import { ProviderPlanId } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import amountWithMinorCurrency from 'features/Onboarding/modules/PricingPlan/helpers/amountWithMinorCurrency';

import PaymentCardsIcon from '../PaymentCardsIcon';
import locales from './locales';
import { RowKey, SectionData, SectionKey } from './types';

const sections: SectionData[] = [
  {
    key: SectionKey.BankAndPayment,
    rows: [
      {
        getCellData: (plan) => {
          const physicalCardCount = plan.limits.physicalPaymentCard.value;

          return {
            asset: (
              <PaymentCardsIcon
                count={physicalCardCount}
                type={plan.paymentCard.type}
              />
            ),
            text: `${physicalCardCount} ${
              plan.paymentCard.type === PaymentCardType.BasicDebitCard
                ? 'Mastercard Basic'
                : 'Mastercard Business World Debit'
            }${
              physicalCardCount > 1
                ? `
            ${locales.perPerson}`
                : ''
            }`,
          };
        },
        key: RowKey.PhysicalCards,
      },
      {
        getCellData: (plan) => {
          let text = locales.optionnal;

          if (plan.limits.virtualPaymentCardMonth.isUnlimited) {
            text = locales.illimited;
          } else if (plan.limits.virtualPaymentCardMonth.value > 0) {
            text = `${plan.limits.virtualPaymentCardMonth.value} ${locales.virtualCardsIncluded}`;
          }

          return {
            asset: (
              <PaymentCardsIcon
                count={plan.limits.virtualPaymentCardMonth.value}
                type="virtual"
                unlimited={plan.limits.virtualPaymentCardMonth.isUnlimited}
              />
            ),
            text,
          };
        },
        key: RowKey.VirtualCards,
      },
      {
        getCellData: (plan) => {
          let text = `${plan.limits.sepaTransferMonth.value}`;

          if (
            plan.providerPlanId === ProviderPlanId.Pro ||
            plan.providerPlanId === ProviderPlanId.Business
          ) {
            text = `**${plan.limits.sepaTransferMonth.value}**`;
          }
          return {
            text,
          };
        },
        key: RowKey.Transfers,
      },
      {
        getCellData: (plan) => {
          let text = `${formatAmount({
            forcePlusSign: false,
            value: plan.paymentCeiling,
            withNoMinorUnitDigits: true,
          })}`;

          if (plan.providerPlanId !== ProviderPlanId.Basic) {
            text = `**${formatAmount({
              forcePlusSign: false,
              value: plan.paymentCeiling,
              withNoMinorUnitDigits: true,
            })}**`;
          }

          if (plan.limits.physicalPaymentCard.value > 1) {
            text += locales.perCard;
          }
          return {
            text,
          };
        },
        key: RowKey.MaximumPaymentMonth,
      },
      {
        getCellData: (plan) => {
          let text = `${formatAmount({
            forcePlusSign: false,
            value: plan.withdrawalCeiling,
            withNoMinorUnitDigits: true,
          })}`;

          if (plan.providerPlanId !== ProviderPlanId.Basic) {
            text = `**${formatAmount({
              forcePlusSign: false,
              value: plan.withdrawalCeiling,
              withNoMinorUnitDigits: true,
            })}**`;
          }

          if (plan.limits.physicalPaymentCard.value > 1) {
            text += locales.perCard;
          }
          return {
            text,
          };
        },
        key: RowKey.MaximumWithdrawalMonth,
      },
      {
        getCellData: (plan) => {
          const text =
            plan.limits.withdrawalMonth.value === 0
              ? locales.optionnal
              : `${plan.limits.withdrawalMonth.value}`;
          return {
            text,
          };
        },
        key: RowKey.WithdrawalMonth,
      },
      {
        getCellData: (plan) => ({
          check: isFeatureAvailable(plan, Feature.BulkTransfers),
        }),
        key: RowKey.BulkTransfers,
      },
      {
        getCellData: () => ({
          text: locales.isComing,
        }),
        isComing: true,
        key: RowKey.SwiftTransfer,
      },
      {
        getCellData: (plan) => {
          if (plan.limits.checkDepositMonth.value > 0) {
            return {
              text: `**${plan.limits.checkDepositMonth.value}**`,
            };
          }
          return {
            check: false,
          };
        },
        key: RowKey.CheckDepositMonth,
      },
      {
        getCellData: (plan) => {
          if (!isFeatureAvailable(plan, Feature.CashDeposit)) {
            return {
              check: false,
            };
          }
          if (plan.providerPlanId === ProviderPlanId.Plus) {
            return {
              text: `${plan.limits.cashDepositMonth.value} x ${formatAmount({
                forcePlusSign: false,
                value: amountWithMinorCurrency(
                  plan.limits.cashDepositMaxAmountPerDeposit.value,
                ),
                withNoMinorUnitDigits: true,
              })} ${locales.perMonth}`,
            };
          }
          return {
            text: `**${plan.limits.cashDepositMonth.value} x ${formatAmount({
              forcePlusSign: false,
              value: amountWithMinorCurrency(
                plan.limits.cashDepositMaxAmountPerDeposit.value,
              ),
              withNoMinorUnitDigits: true,
            })}** ${locales.perMonth}`,
          };
        },
        key: RowKey.CashDepositMonth,
      },
    ],
  },
  {
    key: SectionKey.SpendManagement,
    rows: [
      {
        getCellData: (plan) => {
          if (plan.limits.companyUserAccess.value === 1) {
            return { check: false };
          }
          return {
            text: `${plan.limits.companyUserAccess.value - 1}`,
          };
        },
        key: RowKey.AccessManagement,
      },
      {
        getCellData: (plan) => ({
          check: plan.limits.companyUserAccess.value > 1,
        }),
        key: RowKey.CardsManagement,
      },
      {
        getCellData: (plan) => ({
          check: plan.limits.companyUserAccess.value > 1,
        }),
        key: RowKey.Settings,
      },
      {
        getCellData: (plan) => ({
          check: plan.limits.companyUserAccess.value > 1,
        }),
        key: RowKey.SingleUsageCard,
      },
    ],
  },
  {
    key: SectionKey.SimplifiedComptability,
    rows: [
      {
        getCellData: (plan) => ({
          check: isFeatureAvailable(plan, Feature.TeamManagement),
        }),
        isNew: true,
        key: RowKey.AccountantAccess,
      },
      {
        getCellData: () => ({
          check: true,
        }),
        key: RowKey.Receipts,
      },
      {
        getCellData: () => ({
          check: true,
        }),
        key: RowKey.AccountantExports,
      },
      {
        getCellData: () => ({
          check: true,
        }),
        key: RowKey.AccountantSoftwareIntegration,
      },
    ],
  },
  {
    key: SectionKey.Billing,
    rows: [
      {
        getCellData: () => ({
          check: true,
        }),
        key: RowKey.BillingTool,
      },
      {
        getCellData: () => ({
          check: true,
        }),
        key: RowKey.AutoReminder,
      },
      {
        getCellData: () => ({
          check: true,
        }),
        key: RowKey.InstantPayment,
      },
      {
        getCellData: () => ({
          check: true,
        }),
        key: RowKey.PaymentTerminal,
      },
    ],
  },
  {
    key: SectionKey.SupportAndInsurance,
    rows: [
      {
        getCellData: (plan) => ({
          text:
            plan.customerSupport.type === CustomerSupportType.Standard
              ? `${locales.standardSupport}`
              : `**${locales.prioritySupport}**`,
        }),
        key: RowKey.OnlineSupport,
      },
      {
        getCellData: (plan) => ({
          check: plan.customerSupport.type === CustomerSupportType.PriorityAll,
        }),
        key: RowKey.PhoneSupport,
      },
      {
        getCellData: (plan) => ({
          check: isFeatureAvailable(plan, Feature.InsuranceAssistance),
        }),
        key: RowKey.LegalAssistance,
      },
      {
        getCellData: (plan) => ({
          check: plan.providerPlanId !== ProviderPlanId.Basic,
        }),
        isNew: true,
        key: RowKey.RecoveryAssistance,
      },
      {
        getCellData: (plan) => ({
          check: isFeatureAvailable(plan, Feature.InsuranceAssistance),
        }),
        key: RowKey.AccidentInsurance,
      },
      {
        getCellData: (plan) => ({
          check: isFeatureAvailable(plan, Feature.InsuranceAssistance),
        }),
        key: RowKey.MobileInsurance,
      },
      {
        getCellData: (plan) => ({
          check: isFeatureAvailable(plan, Feature.InsuranceAssistance),
        }),
        key: RowKey.WarrantyExtension,
      },
      {
        getCellData: (plan) => ({
          check:
            plan.providerPlanId === ProviderPlanId.Business ||
            plan.providerPlanId === ProviderPlanId.Pro,
        }),
        isNew: true,
        key: RowKey.ComputerInsurance,
      },
      {
        getCellData: () => ({
          check: true,
        }),
        key: RowKey.ContractLibrary,
      },
      {
        getCellData: () => ({
          check: true,
        }),
        key: RowKey.ToolBox,
      },
      {
        getCellData: () => ({
          check: true,
        }),
        key: RowKey.PartnerNetwork,
      },
    ],
  },
];

export default sections;
